import { useEffect, useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { TextArea } from "@/components/atoms/TextArea";
import { IconButton } from "@/components/atoms/IconButton";
import { Skeleton } from "@/components/atoms/Skeleton";
import { NoteBody, looksLikeHtml, renderTextWithLinks } from "@/lib/renderTextWithLinks";
import { contactsService } from "@/services/contactsService";
import { propertiesService } from "@/services/propertiesService";
import { formatContactNoteDateTime, isContactFileNote, sortContactNotes } from "@/services/notesService";
import type { ContactNoteEntry } from "@/types/contact";

interface BaseProps {
  active: boolean;
  onNoteCreated?: () => void;
  /** When provided, renders a close (X) button in the header (modal usage). */
  onRequestClose?: () => void;
}

interface ContactFileNotesPanelProps extends BaseProps {
  contactId: string | null;
  /** File notes from `GET /contacts/:id` → `contactNotes` (not the top-level `notes` field). */
  contactNotes?: ContactNoteEntry[] | null;
  propertyId?: never;
}

interface PropertyFileNotesPanelProps extends BaseProps {
  propertyId: string | null;
  contactId?: never;
  contactNotes?: never;
}

type Props = ContactFileNotesPanelProps | PropertyFileNotesPanelProps;

const NOTE_PREVIEW_MAX_CHARS = 200;
const NOTE_PREVIEW_MAX_LINES = 4;

/**
 * Truncates a note body to at most {@link NOTE_PREVIEW_MAX_LINES} lines or
 * {@link NOTE_PREVIEW_MAX_CHARS} characters (whichever comes first). `truncated`
 * is true when anything was cut, signalling the "See more" affordance.
 */
function buildNotePreview(text: string): { preview: string; truncated: boolean } {
  let preview = text;
  let truncated = false;

  const lines = preview.split("\n");
  if (lines.length > NOTE_PREVIEW_MAX_LINES) {
    preview = lines.slice(0, NOTE_PREVIEW_MAX_LINES).join("\n");
    truncated = true;
  }
  if (preview.length > NOTE_PREVIEW_MAX_CHARS) {
    preview = preview.slice(0, NOTE_PREVIEW_MAX_CHARS).trimEnd();
    truncated = true;
  }

  return { preview, truncated };
}

function resetComposeState(
  setters: {
    setComposing: (v: boolean) => void;
    setEditingNote: (v: ContactNoteEntry | null) => void;
    setTitle: (v: string) => void;
    setBody: (v: string) => void;
  },
) {
  setters.setComposing(false);
  setters.setEditingNote(null);
  setters.setTitle("");
  setters.setBody("");
}

/**
 * Renders the File notes header, compose/edit form and list of notes.
 * Shared between the inline Timeline panel and the {@link FileNoteModal} wrapper.
 * `active` controls whether the panel loads data and binds keyboard handlers.
 */
export function FileNotesPanel(props: Props) {
  const { active, onNoteCreated, onRequestClose } = props;
  const contactId = "contactId" in props ? props.contactId : null;
  const propertyId = "propertyId" in props ? props.propertyId : null;
  const contactNotes = "contactNotes" in props ? props.contactNotes : undefined;
  const entityId = contactId ?? propertyId;
  const isPropertyMode = Boolean(propertyId);

  const [entries, setEntries] = useState<ContactNoteEntry[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [composing, setComposing] = useState(false);
  const [editingNote, setEditingNote] = useState<ContactNoteEntry | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewingNote, setViewingNote] = useState<ContactNoteEntry | null>(null);

  const isEditMode = Boolean(editingNote);
  const showComposeForm = composing || isEditMode;

  useEffect(() => {
    if (!active) {
      resetComposeState({ setComposing, setEditingNote, setTitle, setBody });
      setEntries([]);
      setViewingNote(null);
      return;
    }

    if (isPropertyMode && propertyId) {
      setLoadingNotes(true);
      propertiesService
        .listFileNotes(propertyId)
        .then((notes) => setEntries(sortContactNotes(notes, "desc")))
        .catch((err) => {
          console.error(err);
          showActionErrorFromUnknown(err, "Could not load file notes.");
          setEntries([]);
        })
        .finally(() => setLoadingNotes(false));
      return;
    }

    setEntries(sortContactNotes(contactNotes, "desc"));
  }, [active, contactNotes, isPropertyMode, propertyId]);

  useEffect(() => {
    if (!active || !entityId || !onRequestClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showComposeForm && !viewingNote) {
        e.stopPropagation();
        onRequestClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active, onRequestClose, entityId, showComposeForm, viewingNote]);

  // When the full-note ("See more") modal is open, Escape must close only it.
  // Stop the event before it reaches Escape handlers on any underlying overlay
  // (e.g. the contact Drawer behind this panel, which closes on bubble phase).
  useEffect(() => {
    if (!viewingNote) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        setViewingNote(null);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [viewingNote]);

  // Escape handling for the compose/edit form: a first Escape blurs the focused
  // input, a second one (nothing focused) hits Cancel. Either way the event is
  // stopped so it never reaches the panel/Drawer close handlers behind it.
  useEffect(() => {
    if (!showComposeForm || viewingNote) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.blur();
      } else {
        resetComposeState({ setComposing, setEditingNote, setTitle, setBody });
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [showComposeForm, viewingNote]);

  if (!active || !entityId) return null;

  const cancelCompose = () => {
    resetComposeState({ setComposing, setEditingNote, setTitle, setBody });
  };

  const startEdit = (row: ContactNoteEntry) => {
    if (!row.id) {
      toast.error("This note cannot be edited (missing id).");
      return;
    }
    setComposing(false);
    setEditingNote(row);
    setTitle(row.title?.trim() ?? "");
    setBody(row.notes?.trim() ?? "");
  };

  const handleSave = async () => {
    const notesText = body.trim();
    if (!notesText) {
      toast.error("Note text is required.");
      return;
    }

    setSaving(true);
    try {
      if (isPropertyMode && propertyId) {
        if (isEditMode && editingNote?.id) {
          const notes = await propertiesService.updatePropertyFileNote(
            propertyId,
            editingNote.id,
            { title: title.trim() || "Note", notes: notesText },
            editingNote,
          );
          setEntries(sortContactNotes(notes, "desc"));
          cancelCompose();
          toast.success("File note updated.");
          onNoteCreated?.();
          return;
        }

        const notes = await propertiesService.addPropertyFileNote(propertyId, {
          title: title.trim() || "Note",
          notes: notesText,
        });
        setEntries(sortContactNotes(notes, "desc"));
        cancelCompose();
        if (notes.length === 0) {
          toast.warn("Note was saved; refresh the property if it does not appear yet.");
        } else {
          toast.success("File note added.");
        }
        onNoteCreated?.();
        return;
      }

      if (!contactId) return;

      if (isEditMode && editingNote?.id) {
        const refreshed = await contactsService.updateContactFileNote(
          contactId,
          editingNote.id,
          { title: title.trim() || "Note", notes: notesText },
          editingNote,
        );
        setEntries(sortContactNotes(refreshed.contactNotes, "desc"));
        cancelCompose();
        toast.success("File note updated.");
        onNoteCreated?.();
        return;
      }

      const refreshed = await contactsService.addContactFileNote(contactId, {
        title: title.trim() || "Note",
        notes: notesText,
      });
      const notes = sortContactNotes(refreshed.contactNotes, "desc");
      setEntries(notes);
      cancelCompose();
      if (notes.length === 0) {
        toast.warn("Note was saved; refresh the contact if it does not appear yet.");
      } else {
        toast.success("File note added.");
      }
      onNoteCreated?.();
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(
        err,
        isEditMode
          ? "Could not update file note. Please try again."
          : "Could not create file note. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const renderNoteForm = (label: string) => (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-bridge-muted">{label}</p>
      <div>
        <label className="text-xs font-medium text-bridge-muted block mb-1">Title</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Follow-up call"
          disabled={saving}
          className="w-full"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-bridge-muted block mb-1">
          Note <span className="text-red-500">*</span>
        </label>
        <TextArea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Enter note text…"
          disabled={saving}
          className="w-full"
        />
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <Button type="button" variant="secondary" size="sm" disabled={saving} onClick={cancelCompose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" size="sm" loading={saving} onClick={handleSave}>
          {isEditMode ? "Save changes" : "Save note"}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <header className="flex items-center justify-between gap-3 border-b border-bridge-border/60 px-5 py-4">
        <h2 className="text-base font-semibold text-bridge-fg">File notes</h2>
        <div className="flex items-center gap-1">
          <IconButton
            label="Add file note"
            type="button"
            disabled={saving || showComposeForm || loadingNotes}
            onClick={() => {
              setEditingNote(null);
              setTitle("");
              setBody("");
              setComposing(true);
            }}
            className="text-bridge-accent hover:text-bridge-accent hover:bg-bridge-accent/10"
          >
            <Plus size={18} />
          </IconButton>
          {onRequestClose ? (
            <button
              type="button"
              onClick={onRequestClose}
              className="rounded-md p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface-hover hover:text-bridge-fg"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          ) : null}
        </div>
      </header>

      <div className="min-h-[200px] flex-1 space-y-6 overflow-y-auto px-5 py-4">
        {loadingNotes ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : null}

        {!loadingNotes && composing ? (
          <section className="rounded-md border border-bridge-accent/30 bg-bridge-accent/5 p-4">
            {renderNoteForm("New file note")}
          </section>
        ) : null}

        {!loadingNotes && entries.length === 0 && !showComposeForm ? (
          <p className="text-sm text-bridge-muted">No file notes to display.</p>
        ) : null}

        {!loadingNotes && entries.length > 0 ? (
          <section>
            <ul className="space-y-4">
              {entries.map((row, index) => {
                const noteBody = (row.notes ?? "").trim();
                const heading = row.title?.trim() || "Note";
                const when = formatContactNoteDateTime(
                  row.created?.trim() || row.modified?.trim() || "",
                );
                const isBeingEdited = editingNote?.id === row.id;
                const { preview: notePreview, truncated: noteTruncated } = buildNotePreview(noteBody);
                return (
                  <li
                    key={row.id || `note-${index}`}
                    className={
                      isBeingEdited
                        ? "rounded-md border border-bridge-accent/30 bg-bridge-accent/5 p-4 shadow-sm"
                        : "rounded-md border border-bridge-border/60 bg-gray-100 p-4 shadow-sm"
                    }
                  >
                    {isBeingEdited ? (
                      renderNoteForm("Edit file note")
                    ) : (
                      <>
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-bridge-fg">{heading}</p>
                            {when ? <p className="text-xs text-bridge-muted mt-0.5">{when}</p> : null}
                          </div>
                          {row.id && isContactFileNote(row) && !showComposeForm ? (
                            <IconButton
                              label="Edit file note"
                              type="button"
                              onClick={() => startEdit(row)}
                              className="shrink-0 text-bridge-muted hover:text-bridge-accent hover:bg-bridge-accent/10"
                            >
                              <Pencil size={16} />
                            </IconButton>
                          ) : null}
                        </div>
                        {noteBody ? (
                          looksLikeHtml(noteBody) ? (
                            <NoteBody text={noteBody} />
                          ) : (
                            <div className="text-sm leading-relaxed text-bridge-fg whitespace-pre-wrap break-words">
                              {renderTextWithLinks(noteTruncated ? `${notePreview}…` : notePreview)}
                              {noteTruncated ? (
                                <button
                                  type="button"
                                  onClick={() => setViewingNote(row)}
                                  className="ml-1 font-medium text-bridge-accent underline underline-offset-2 hover:text-bridge-accent/80"
                                >
                                  See more
                                </button>
                              ) : null}
                            </div>
                          )
                        ) : (
                          <p className="text-sm italic text-bridge-muted">No note text.</p>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>

      {viewingNote ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-fadeIn"
            onClick={() => setViewingNote(null)}
            aria-hidden
          />

          <div
            className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-bridge-border/70 bg-bridge-surface shadow-glass"
            role="dialog"
            aria-label="File note"
          >
            <header className="flex items-start justify-between gap-3 border-b border-bridge-border/60 px-5 py-4">
              <div>
                <p className="text-base font-semibold text-bridge-fg">
                  {viewingNote.title?.trim() || "Note"}
                </p>
                {(() => {
                  const when = formatContactNoteDateTime(
                    viewingNote.created?.trim() || viewingNote.modified?.trim() || "",
                  );
                  return when ? <p className="text-xs text-bridge-muted mt-0.5">{when}</p> : null;
                })()}
              </div>
              <button
                type="button"
                onClick={() => setViewingNote(null)}
                className="rounded-md p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface-hover hover:text-bridge-fg"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="rounded-md border border-bridge-border/60 bg-gray-100 p-4 shadow-sm">
                {(viewingNote.notes ?? "").trim() ? (
                  <NoteBody text={(viewingNote.notes ?? "").trim()} />
                ) : (
                  <p className="text-sm italic text-bridge-muted">No note text.</p>
                )}
              </div>
            </div>

            <footer className="flex justify-center border-t border-bridge-border/60 px-5 py-3">
              <Button variant="primary" type="button" onClick={() => setViewingNote(null)}>
                Close
              </Button>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
}
